// A picked Garmin export zip's upload, straight to the bucket (spec
// 2026-10-29-garmin-history-import, D6): the LiveView starts a multipart
// upload and answers with a presigned URL per part; the browser PUTs the
// parts, two at a time, keeping each answer's ETag; then the LiveView
// completes the upload. The hook (GarminHistoryUploadHook) wires this to the
// page; everything it does is injected, so the logic is tested without a DOM.

export interface StartReply {
  ok?: boolean
  error?: string
  import_id?: number
  part_bytes?: number
  part_urls?: string[]
}

export interface Reply {
  ok?: boolean
  error?: string
}

export interface UploadedPart {
  part_number: number
  etag: string
}

export interface UploadPartsOptions {
  file: Blob
  partBytes: number
  partUrls: string[]
  signal: AbortSignal
  onProgress: (uploadedBytes: number) => void
}

export type PushEvent = (event: string, payload: Record<string, unknown>) => Promise<unknown>

export interface HistoryUploadDeps {
  pushEvent: PushEvent
  sleep: (ms: number) => Promise<void>
  uploadParts: (options: UploadPartsOptions) => Promise<UploadedPart[]>
}

export const maxCompleteAttempts = 5
const completeRetryMs = 2000

interface CurrentUpload {
  importId: number
  controller: AbortController
  // The whole percent last sent.
  percent: number
}

export interface HistoryUploader {
  start: (file: Blob) => Promise<void>
  abort: (importId: number) => void
  stop: () => void
  reportState: () => void
}

export function createHistoryUploader(deps: HistoryUploadDeps): HistoryUploader {
  let current: CurrentUpload | null = null

  function stop() {
    if (current) {
      current.controller.abort()
      current = null
    }
  }

  // The LiveView asks to stop an upload it cancelled. Only that import's:
  // a new upload may have started meanwhile.
  function abort(importId: number) {
    if (current && current.importId === importId) {
      stop()
    }
  }

  function finish(upload: CurrentUpload) {
    if (current === upload) {
      current = null
    }
  }

  async function start(file: Blob) {
    // Picking the zip again starts over (D19).
    stop()

    let reply: StartReply
    try {
      reply = (await deps.pushEvent('history_upload_start', { size: file.size })) as StartReply
    } catch (_error) {
      return
    }

    if (!reply || !reply.ok || reply.import_id == null || !reply.part_urls || !reply.part_bytes) {
      return
    }

    const upload: CurrentUpload = {
      importId: reply.import_id,
      controller: new AbortController(),
      percent: 0,
    }
    current = upload

    let parts: UploadedPart[]
    try {
      parts = await deps.uploadParts({
        file,
        partBytes: reply.part_bytes,
        partUrls: reply.part_urls,
        signal: upload.controller.signal,
        onProgress: progressReporter(upload, file.size),
      })
    } catch (_error) {
      if (upload.controller.signal.aborted) {
        return
      }

      finish(upload)
      // Stops the parts still uploading: the upload can't complete.
      upload.controller.abort()
      await pushQuietly('history_upload_failed', { import_id: upload.importId })
      return
    }

    if (upload.controller.signal.aborted) {
      return
    }

    await complete(upload, parts)
    finish(upload)
  }

  // Sends the progress as a whole percent, only when it changes.
  function progressReporter(upload: CurrentUpload, size: number) {
    return function reportProgress(uploadedBytes: number) {
      if (upload.controller.signal.aborted || size <= 0) {
        return
      }

      const percent = Math.min(100, Math.floor((uploadedBytes / size) * 100))
      if (percent === upload.percent) {
        return
      }

      upload.percent = percent
      sendProgress(upload)
    }
  }

  function sendProgress(upload: CurrentUpload) {
    pushQuietly('history_upload_progress', { import_id: upload.importId, percent: upload.percent })
  }

  // A LiveView that remounted (a reconnect) knows nothing of this tab's
  // upload until told: its card would say the upload didn't finish. Sends
  // the running upload's progress again, through the completion's retries.
  function reportState() {
    if (current) {
      sendProgress(current)
    }
  }

  // `storage_failed` leaves the import uploading for another try, and a push
  // that fails (no connection) may not have reached the server: both are sent
  // again. Any other answer is final. When the tries run out, the LiveView is
  // told, so the card stops showing the upload as running.
  async function complete(upload: CurrentUpload, parts: UploadedPart[]) {
    for (let attempt = 1; attempt <= maxCompleteAttempts; attempt++) {
      const retry = await completeOnce(upload, parts)
      if (!retry) {
        return
      }

      if (attempt < maxCompleteAttempts) {
        await deps.sleep(completeRetryMs * attempt)
      }

      if (upload.controller.signal.aborted) {
        return
      }
    }

    await pushQuietly('history_upload_stalled', { import_id: upload.importId })
  }

  // Whether the completion should be sent again.
  async function completeOnce(upload: CurrentUpload, parts: UploadedPart[]): Promise<boolean> {
    let reply: Reply
    try {
      reply = (await deps.pushEvent('history_upload_complete', {
        import_id: upload.importId,
        parts,
      })) as Reply
    } catch (_error) {
      return true
    }

    return Boolean(reply && reply.error === 'storage_failed')
  }

  // A push whose failure changes nothing: the LiveView remounts on rejoin.
  async function pushQuietly(event: string, payload: Record<string, unknown>) {
    try {
      await deps.pushEvent(event, payload)
    } catch (_error) {
      return
    }
  }

  return { start, abort, stop, reportState }
}

export type PutPart = (
  url: string,
  body: Blob,
  signal: AbortSignal,
  onProgress: (loadedBytes: number) => void,
) => Promise<string>

const partAttempts = 3
const partRetryMs = 1000
export const partConcurrency = 2

export interface UploadPartsDeps {
  putPart: PutPart
  sleep: (ms: number) => Promise<void>
}

// Uploads every part, `partConcurrency` at a time, each tried a few times.
// Rejects with the first part that fails every try, or when aborted.
export function uploadParts(
  options: UploadPartsOptions,
  deps: UploadPartsDeps,
): Promise<UploadedPart[]> {
  const loaded: number[] = options.partUrls.map(function noBytes() {
    return 0
  })
  const results: UploadedPart[] = []
  let next = 0

  function reportProgress() {
    const total = loaded.reduce(function sum(acc, bytes) {
      return acc + bytes
    }, 0)
    options.onProgress(total)
  }

  async function uploadPart(index: number) {
    const url = options.partUrls[index]
    if (url === undefined) {
      throw new Error(`no URL for part ${index + 1}`)
    }

    const start = index * options.partBytes
    const body = options.file.slice(start, start + options.partBytes)

    for (let attempt = 1; ; attempt++) {
      if (options.signal.aborted) {
        throw new Error('aborted')
      }

      try {
        const etag = await deps.putPart(
          url,
          body,
          options.signal,
          function onPartProgress(bytes: number) {
            loaded[index] = bytes
            reportProgress()
          },
        )
        loaded[index] = body.size
        reportProgress()
        results.push({ part_number: index + 1, etag })
        return
      } catch (error) {
        loaded[index] = 0
        if (options.signal.aborted || attempt >= partAttempts) {
          throw error
        }
        await deps.sleep(partRetryMs * attempt)
      }
    }
  }

  async function worker() {
    while (next < options.partUrls.length) {
      const index = next
      next += 1
      await uploadPart(index)
    }
  }

  const workers = []
  for (let i = 0; i < Math.min(partConcurrency, options.partUrls.length); i++) {
    workers.push(worker())
  }

  return Promise.all(workers).then(function sortedParts() {
    return results.sort(function byPartNumber(a, b) {
      return a.part_number - b.part_number
    })
  })
}

// A part's PUT with XMLHttpRequest, for its upload progress. The bucket must
// expose `ETag` to the page (its CORS rules).
export function xhrPutPart(
  url: string,
  body: Blob,
  signal: AbortSignal,
  onProgress: (loadedBytes: number) => void,
): Promise<string> {
  return new Promise(function putPart(resolve, reject) {
    const xhr = new XMLHttpRequest()

    function onAbort() {
      xhr.abort()
    }

    xhr.open('PUT', url)
    xhr.upload.onprogress = function onUploadProgress(event) {
      onProgress(event.loaded)
    }
    xhr.onload = function onLoad() {
      signal.removeEventListener('abort', onAbort)
      const etag = xhr.getResponseHeader('ETag')

      if (xhr.status >= 200 && xhr.status < 300 && etag) {
        resolve(etag)
      } else {
        reject(new Error(`part upload failed: ${xhr.status}`))
      }
    }
    xhr.onerror = function onError() {
      signal.removeEventListener('abort', onAbort)
      reject(new Error('part upload failed'))
    }
    xhr.onabort = function onXhrAbort() {
      signal.removeEventListener('abort', onAbort)
      reject(new Error('aborted'))
    }

    signal.addEventListener('abort', onAbort)
    xhr.send(body)
  })
}

export function sleep(ms: number): Promise<void> {
  return new Promise(function wait(resolve) {
    setTimeout(resolve, ms)
  })
}
